import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { FileText, Loader2, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { documentApiConnect } from '../lib/api/documentApi.connect';
export function DocumentPreviewModal({ document, onClose }) {
    const [content, setContent] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    useEffect(()=>{
        const fetchContent = async ()=>{
            if (document.status !== 'ready') {
                setError('Document is not ready for preview');
                setLoading(false);
                return;
            }
            try {
                const result = await documentApiConnect.downloadDocument(document.id);
                const text = await result.blob.text();
                setContent(text);
                setError(null);
            } catch (err) {
                console.error('Preview error:', err);
                setError(err instanceof Error ? err.message : 'Failed to load document');
            } finally{
                setLoading(false);
            }
        };
        fetchContent();
    }, [
        document.id,
        document.status
    ]);
    // Close on Escape key
    useEffect(()=>{
        const handleKeyDown = (e)=>{
            if (e.key === 'Escape') onClose();
        };
        window.addEventListener('keydown', handleKeyDown);
        return ()=>window.removeEventListener('keydown', handleKeyDown);
    }, [
        onClose
    ]);
    return /*#__PURE__*/ _jsx("div", {
        className: "fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4",
        children: /*#__PURE__*/ _jsxs("div", {
            className: "bg-white dark:bg-gray-800 rounded-lg w-full max-w-4xl max-h-[90vh] flex flex-col shadow-2xl",
            children: [
                /*#__PURE__*/ _jsxs("div", {
                    className: "flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700",
                    children: [
                        /*#__PURE__*/ _jsxs("div", {
                            className: "flex items-center gap-2",
                            children: [
                                /*#__PURE__*/ _jsx(FileText, {
                                    className: "w-5 h-5 text-gray-500"
                                }),
                                /*#__PURE__*/ _jsx("h2", {
                                    className: "text-lg font-semibold text-gray-900 dark:text-gray-100 truncate",
                                    children: document.filename
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsx("button", {
                            onClick: onClose,
                            "aria-label": "Close",
                            className: "text-gray-500 hover:text-gray-700 dark:hover:text-gray-300",
                            children: /*#__PURE__*/ _jsx(X, {
                                className: "w-5 h-5"
                            })
                        })
                    ]
                }),
                /*#__PURE__*/ _jsxs("div", {
                    className: "flex-1 overflow-y-auto p-6",
                    children: [
                        loading && /*#__PURE__*/ _jsxs("div", {
                            className: "flex items-center justify-center h-64",
                            children: [
                                /*#__PURE__*/ _jsx(Loader2, {
                                    className: "w-8 h-8 animate-spin text-indigo-500"
                                }),
                                /*#__PURE__*/ _jsx("span", {
                                    className: "ml-2 text-gray-600 dark:text-gray-400",
                                    children: "Loading preview..."
                                })
                            ]
                        }),
                        error && /*#__PURE__*/ _jsx("div", {
                            className: "flex items-center justify-center h-64",
                            children: /*#__PURE__*/ _jsx("p", {
                                className: "text-red-500",
                                children: error
                            })
                        }),
                        !loading && !error && content && /*#__PURE__*/ _jsx("div", {
                            className: "prose dark:prose-invert max-w-none",
                            children: document.contentType === 'application/json' ? /*#__PURE__*/ _jsx("pre", {
                                className: "bg-gray-100 dark:bg-gray-900 p-4 rounded-lg overflow-x-auto",
                                children: /*#__PURE__*/ _jsx("code", {
                                    children: content
                                })
                            }) : /*#__PURE__*/ _jsx(ReactMarkdown, {
                                children: content
                            })
                        })
                    ]
                }),
                /*#__PURE__*/ _jsxs("div", {
                    className: "border-t border-gray-200 dark:border-gray-700 px-6 py-4 flex justify-end gap-3",
                    children: [
                        /*#__PURE__*/ _jsx("button", {
                            onClick: onClose,
                            "aria-label": "Close preview",
                            className: "px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600",
                            children: "Close"
                        }),
                        !loading && !error && content && /*#__PURE__*/ _jsx("button", {
                            onClick: async ()=>{
                                const result = await documentApiConnect.downloadDocument(document.id);
                                const url = window.URL.createObjectURL(result.blob);
                                const link = window.document.createElement('a');
                                link.href = url;
                                link.setAttribute('download', result.filename);
                                window.document.body.appendChild(link);
                                link.click();
                                link.remove();
                                window.URL.revokeObjectURL(url);
                            },
                            className: "px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700",
                            children: "Download"
                        })
                    ]
                })
            ]
        })
    });
}
